import { PORTAL_NUMBER_LOCALE, type PortalTranslate } from '@/lib/i18n/portalLocale';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';

/**
 * An Institution's weekly Study report as shared with a parent (SIR-VIS-05). Counts only:
 * no transcripts, answers, file names or private Study content. The Institution's name and logo
 * appear with "powered by Airbotix" (co-branding, not white-label).
 */
export interface SharedInstitutionStudyReport {
  institution: { display_name: string; logo_url: string | null };
  learner: { nickname: string };
  period: { start: string; end_exclusive: string };
  expires_at: string;
  powered_by: 'Airbotix';
  content: {
    summary?: {
      active_days?: number;
      completed_sessions?: number;
      tool_uses?: number;
      verified_checks?: number;
      verified_correct?: number;
      practice_attempts?: number;
      practice_correct?: number;
      assignments_assigned?: number;
      assignments_completed?: number;
      tutor_turns?: number;
    };
    subjects?: Array<{
      subject_key: string;
      display_name: string;
      tool_uses: number;
      practice_attempts: number;
      tutor_turns: number;
    }>;
    assignments?: Array<{
      title: string;
      status: 'assigned' | 'started' | 'completed';
      due_at: string | null;
    }>;
  };
}

const STATUS_LABEL = {
  assigned: 'Not started',
  started: 'Started',
  completed: 'Completed',
} as const;

function day(value: string, numberLocale: string) {
  return new Intl.DateTimeFormat(numberLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

function count(value: number | undefined, t: PortalTranslate) {
  return value === undefined ? t('Unavailable') : String(value);
}

export function InstitutionStudyReportView({ report }: { report: SharedInstitutionStudyReport }) {
  const { t, locale } = usePortalLocale();
  const numberLocale = PORTAL_NUMBER_LOCALE[locale];
  const summary = report.content.summary ?? {};
  const checked = (summary.verified_checks ?? 0) + (summary.practice_attempts ?? 0);
  const correct = (summary.verified_correct ?? 0) + (summary.practice_correct ?? 0);
  const lastDay = new Date(
    new Date(report.period.end_exclusive).getTime() - 86_400_000,
  ).toISOString();
  return (
    <article className="card-base" aria-label={t('{name} Study report', { name: report.institution.display_name })}>
      <header className="flex flex-wrap items-center gap-4">
        {report.institution.logo_url && (
          <img
            src={report.institution.logo_url}
            alt={t('{name} logo', { name: report.institution.display_name })}
            className="h-12 w-auto"
          />
        )}
        <div>
          <p className="eyebrow eyebrow-mint">{report.institution.display_name}</p>
          <h2 className="mt-1 text-[24px] font-bold text-ink">
            {t("{name}'s Study week", { name: report.learner.nickname })}
          </h2>
          <p className="text-sm text-ink-soft">
            {t('{start} to {end}', {
              start: day(report.period.start, numberLocale),
              end: day(lastDay, numberLocale),
            })}
          </p>
        </div>
      </header>

      <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          [t('Active days'), count(summary.active_days, t)],
          [t('Study sessions'), count(summary.completed_sessions, t)],
          [t('Tool uses'), count(summary.tool_uses, t)],
          [
            t('Checked answers correct'),
            checked === 0 ? t('None checked') : t('{correct} of {checked}', { correct, checked }),
          ],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[20px] bg-surface-soft p-4">
            <dt className="text-xs font-bold uppercase tracking-wide text-slate2">{label}</dt>
            <dd className="mt-1 text-[22px] font-extrabold text-ink">{value}</dd>
          </div>
        ))}
      </dl>

      {!!report.content.subjects?.length && (
        <section className="mt-5" aria-label={t('Subjects')}>
          <h3 className="text-sm font-extrabold text-ink">{t('Subjects')}</h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {report.content.subjects.map((subject) => (
              <li
                key={subject.subject_key}
                className="rounded-[18px] border border-hairline p-3 text-sm text-ink"
              >
                <strong className="block">{subject.display_name}</strong>
                {t('{tools} tool uses · {questions} practice questions · {turns} Tutor turns', {
                  tools: subject.tool_uses,
                  questions: subject.practice_attempts,
                  turns: subject.tutor_turns,
                })}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!!report.content.assignments?.length && (
        <section className="mt-5" aria-label={t('Tasks from the Institution')}>
          <h3 className="text-sm font-extrabold text-ink">
            {t('Tasks from {name}', { name: report.institution.display_name })}
          </h3>
          <ul className="mt-2 divide-y divide-hairline text-sm text-ink">
            {report.content.assignments.map((task) => (
              <li key={`${task.title}-${task.due_at ?? ''}`} className="py-2">
                {task.title} · {t(STATUS_LABEL[task.status])}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-5 text-xs text-slate2">
        {t(
          "Counts only. Mastery levels are not assessed yet. This report does not include Tutor conversations, answers, uploaded files or {name}'s private Study.",
          { name: report.learner.nickname },
        )}{' '}
        {t('Link available until {date}.', { date: day(report.expires_at, numberLocale) })}
      </p>
      <p className="mt-2 text-xs font-bold text-slate2">
        {t('Powered by {name}', { name: report.powered_by })}
      </p>
    </article>
  );
}
